import { useState, useRef, useEffect } from 'react';
import { useAuthController } from '@/hooks/useAuthController';
import { Card } from '@/components/shared/card';
import { Alert, AlertDescription } from "@/components/shared/alert";
import { Button } from '@/components/shared/button';
import { Input } from '@/components/shared/input';
import { Label } from '@/components/shared/label';
import { AuthError } from '@supabase/supabase-js';
import MainLayout from '@/components/layout/MainLayout';
import { ROUTES } from '@/config/routes';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';

const SignUp = () => {
  const { signUp, status, isLoading } = useAuthController();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Focus management
  useEffect(() => {
    if (error && emailRef.current) {
      emailRef.current.focus();
    }
  }, [error]);

  const handleAuthError = (error: AuthError) => {
    console.error('Auth error:', error);
    let errorMessage = 'An error occurred during sign up.';
    
    if (error instanceof AuthError) {
      switch (error.message) {
        case 'User already registered':
          errorMessage = 'An account with this email already exists. Please sign in instead.';
          break;
        case 'Password should be at least 6 characters':
          errorMessage = 'Password must be at least 6 characters long.';
          break;
        case 'Invalid email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'Too many requests':
          errorMessage = 'Too many sign-up attempts. Please wait a moment before trying again.';
          break;
        default:
          errorMessage = error.message;
      }
    }
    
    setError(errorMessage);
    setMessage(null);
  };

  const validateForm = () => {
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!validateForm()) {
      return;
    }

    const { error } = await signUp(email, password);
    
    if (error) {
      handleAuthError(error);
    } else {
      setMessage('Creating your account...');
      // The AuthController will handle the redirect
    }
  };

  const isFormDisabled = isLoading || status === 'loading';

  return (
    <MainLayout>
      <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="mt-6 text-3xl font-bold tracking-tight text-foreground">
              Create Account
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign up to save and track your assessments
            </p>
          </div>
          
          <Card className="mt-8 p-6 bg-card">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="mb-6" role="alert" aria-live="polite">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {message && (
                <Alert className="mb-6" role="status" aria-live="polite">
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="block text-sm font-medium text-foreground">
                    Email address
                  </Label>
                  <Input
                    ref={emailRef}
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isFormDisabled}
                    className="mt-1 block w-full"
                    aria-describedby={error ? "error-message" : undefined}
                    aria-invalid={!!error}
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="block text-sm font-medium text-foreground">
                    Password
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      ref={passwordRef}
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isFormDisabled}
                      className="block w-full pr-10"
                      aria-describedby={error ? "error-message" : undefined}
                      aria-invalid={!!error}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isFormDisabled}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground">
                    Confirm Password
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isFormDisabled}
                      className="block w-full pr-10"
                      aria-describedby={error ? "error-message" : undefined}
                      aria-invalid={!!error}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isFormDisabled}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isFormDisabled}
                className="w-full"
                aria-busy={isFormDisabled}
              >
                {isFormDisabled ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link 
                    to={ROUTES.LOGIN}
                    className="font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default SignUp;
